import {
  AlertTriangle,
  AppWindow,
  BadgeCheck,
  Boxes,
  Check,
  ChevronRight,
  ClipboardCheck,
  Cpu,
  Gauge,
  Globe2,
  HardDrive,
  Network,
  PackageCheck,
  Radio,
  Router,
  ServerCog,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Wifi,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function StbBuyersChecklistPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Boxes, label: messages.sbcStat1 },
    { icon: ClipboardCheck, label: messages.sbcStat2 },
    { icon: ServerCog, label: messages.sbcStat3 },
  ];

  const buyerTypes: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: Radio, title: messages.sbcBuyer1Title, body: messages.sbcBuyer1Body },
    { icon: Globe2, title: messages.sbcBuyer2Title, body: messages.sbcBuyer2Body },
    { icon: Network, title: messages.sbcBuyer3Title, body: messages.sbcBuyer3Body },
    { icon: BadgeCheck, title: messages.sbcBuyer4Title, body: messages.sbcBuyer4Body },
    { icon: ShoppingCart, title: messages.sbcBuyer5Title, body: messages.sbcBuyer5Body },
  ];

  const tableRows = [
    {
      icon: Cpu,
      check: messages.sbcRow1Check,
      why: messages.sbcRow1Why,
      note: messages.sbcRow1Note,
    },
    {
      icon: HardDrive,
      check: messages.sbcRow2Check,
      why: messages.sbcRow2Why,
      note: messages.sbcRow2Note,
    },
    {
      icon: Gauge,
      check: messages.sbcRow3Check,
      why: messages.sbcRow3Why,
      note: messages.sbcRow3Note,
    },
    {
      icon: Router,
      check: messages.sbcRow4Check,
      why: messages.sbcRow4Why,
      note: messages.sbcRow4Note,
    },
    {
      icon: Wifi,
      check: messages.sbcRow5Check,
      why: messages.sbcRow5Why,
      note: messages.sbcRow5Note,
    },
    {
      icon: ShieldCheck,
      check: messages.sbcRow6Check,
      why: messages.sbcRow6Why,
      note: messages.sbcRow6Note,
    },
    {
      icon: AppWindow,
      check: messages.sbcRow7Check,
      why: messages.sbcRow7Why,
      note: messages.sbcRow7Note,
    },
    {
      icon: ServerCog,
      check: messages.sbcRow8Check,
      why: messages.sbcRow8Why,
      note: messages.sbcRow8Note,
    },
    {
      icon: ClipboardCheck,
      check: messages.sbcRow9Check,
      why: messages.sbcRow9Why,
      note: messages.sbcRow9Note,
    },
    {
      icon: PackageCheck,
      check: messages.sbcRow10Check,
      why: messages.sbcRow10Why,
      note: messages.sbcRow10Note,
    },
    {
      icon: Truck,
      check: messages.sbcRow11Check,
      why: messages.sbcRow11Why,
      note: messages.sbcRow11Note,
    },
    {
      icon: BadgeCheck,
      check: messages.sbcRow12Check,
      why: messages.sbcRow12Why,
      note: messages.sbcRow12Note,
    },
  ];

  const modelFits = [
    {
      name: "inext TV6 Ultra",
      body: messages.sbcModelTv6UltraBody,
      href: "/products/inext-tv6-ultra",
      meta: messages.sbcModelTv6UltraMeta,
    },
    {
      name: "inext TV6",
      body: messages.sbcModelTv6Body,
      href: "/products/inext-tv6",
      meta: messages.sbcModelTv6Meta,
    },
    {
      name: "inext TV5 Ultra",
      body: messages.sbcModelTv5UltraBody,
      href: "/products/inext-tv5-ultra",
      meta: messages.sbcModelTv5UltraMeta,
    },
    {
      name: "inext TV5",
      body: messages.sbcModelTv5Body,
      href: "/products/inext-tv5",
      meta: messages.sbcModelTv5Meta,
    },
  ];

  const redFlags = [
    messages.sbcRedFlag1,
    messages.sbcRedFlag2,
    messages.sbcRedFlag3,
    messages.sbcRedFlag4,
    messages.sbcRedFlag5,
    messages.sbcRedFlag6,
    messages.sbcRedFlag7,
  ];

  const sampleChecks = [
    messages.sbcSample1,
    messages.sbcSample2,
    messages.sbcSample3,
    messages.sbcSample4,
    messages.sbcSample5,
    messages.sbcSample6,
    messages.sbcSample7,
    messages.sbcSample8,
    messages.sbcSample9,
    messages.sbcSample10,
  ];

  const faqs = [
    { q: messages.sbcFaqQ1, a: messages.sbcFaqA1 },
    { q: messages.sbcFaqQ2, a: messages.sbcFaqA2 },
    { q: messages.sbcFaqQ3, a: messages.sbcFaqA3 },
    { q: messages.sbcFaqQ4, a: messages.sbcFaqA4 },
    { q: messages.sbcFaqQ5, a: messages.sbcFaqA5 },
    { q: messages.sbcFaqQ6, a: messages.sbcFaqA6 },
    { q: messages.sbcFaqQ7, a: messages.sbcFaqA7 },
  ];

  return (
    <SiteLayout>
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.sbcBreadcrumbPage}</span>
        </nav>
      </div>

      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.sbcHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.sbcHeroTitle}{" "}
            <span className="text-gradient">{messages.sbcHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.sbcHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.sbcHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.sbcHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#checklist">{messages.sbcHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.sbcThesisEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.sbcThesisTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.sbcThesisBody1}</p>
            <p>{messages.sbcThesisBody2}</p>
          </div>
          <div className="mt-8 rounded-2xl border border-primary/25 bg-primary/[0.04] p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ClipboardCheck className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-foreground">
                  {messages.sbcThesisNoteTitle}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages.sbcThesisNoteBody}
                </p>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.sbcBuyerEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.sbcBuyerTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.sbcBuyerIntro}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {buyerTypes.map(({ icon: Icon, title, body }, index) => (
              <article
                key={title}
                className={`rounded-2xl border border-border/60 bg-card/60 p-6 ${
                  index === buyerTypes.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </RevealSection>

      <RevealSection id="checklist" className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.sbcTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.sbcTableTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.sbcTableIntro}
            </p>
          </div>
          <ol className="mt-10 grid gap-3 md:hidden">
            {tableRows.map(({ icon: Icon, check, why, note }) => (
              <li key={check} className="rounded-xl border border-border/60 bg-card/60 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="pt-1.5 text-base font-semibold text-foreground">{check}</h3>
                </div>
                <dl className="mt-4 space-y-4 border-t border-border/60 pt-4">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {messages.sbcTableColWhy}
                    </dt>
                    <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{why}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {messages.sbcTableColNote}
                    </dt>
                    <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{note}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ol>
          <div className="mt-10 hidden overflow-x-auto rounded-2xl border border-border/60 bg-card/60 md:block">
            <table className="w-full min-w-[780px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.sbcTableColCheck}
                  </th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.sbcTableColWhy}
                  </th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.sbcTableColNote}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map(({ icon: Icon, check, why, note }) => (
                  <tr key={check} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="px-4 py-4 text-left font-medium text-foreground/90">
                      <span className="inline-flex items-start gap-2.5">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span>{check}</span>
                      </span>
                    </th>
                    <td className="px-4 py-4 leading-relaxed text-muted-foreground">{why}</td>
                    <td className="px-4 py-4 leading-relaxed text-muted-foreground">{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.sbcTableNote}</p>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.sbcModelEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.sbcModelTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.sbcModelIntro}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {modelFits.map((model) => (
              <a
                key={model.name}
                href={u(model.href)}
                className="group flex flex-col rounded-2xl border border-border/60 bg-card/70 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Boxes className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
                  {model.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{model.body}</p>
                <span className="mt-auto pt-5 text-xs font-semibold uppercase tracking-wider text-primary">
                  {model.meta}
                </span>
              </a>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button asChild variant="outline">
              <a href={u("/products")}>{messages.sbcModelCta}</a>
            </Button>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.sbcRedFlagsEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.sbcRedFlagsTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {messages.sbcRedFlagsIntro}
          </p>
          <ul className="mt-6 space-y-3">
            {redFlags.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <span className="text-sm text-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.sbcSampleEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.sbcSampleTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.sbcSampleIntro}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sampleChecks.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm text-foreground/90">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-primary/25 bg-card/70 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  {messages.sbcFitEyebrow}
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  {messages.sbcFitTitle}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {messages.sbcFitBody}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild size="sm">
                    <a href={u("/solutions/alcatraz-dms")}>{messages.sbcFitCtaDms}</a>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <a href={u("/solutions/logistics")}>{messages.sbcFitCtaLogistics}</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.sbcFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.sbcFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.sbcCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.sbcCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.sbcCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.sbcCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.sbcCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
